import I18n from 'I18n';
import { SearchWithAutocompleteProvider } from './SearchInputWithAutocompleteProvider';
import getLang from 'academy-common-lib/utils/getLang';
import useSearchParams from 'academy-common-lib/hooks/useSearchParams';
import useDebouncedValue from 'academy-common-lib/hooks/useDebouncedValue';
import { useSearchAutocomplete } from '../autocompleteQueries';
import { useDisplayedData } from '../../table/tableHooks';
import { useEffect, useMemo, useState } from 'react';
import UsageTracker from '../../../utils/UsageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
const SEARCH_DEBOUNCE_TIMEOUT = 400;
const SEARCH_MIN_QUERY_SIZE = 2;
const mapSuggestionsToAutocomplete = ({
  searchValue,
  popularTerms,
  quickLinks
}) => {
  const searchQueryTerm = {
    term: searchValue,
    language: getLang(),
    value: searchValue,
    index: 0
  };
  return [popularTerms.length ? {
    text: I18n.text('learningCenter.autocomplete.popularSearches').toUpperCase(),
    value: 'popularSearches',
    options: [searchQueryTerm,
    // search term is the 1st option
    ...popularTerms.filter(({
      term
    }) => term.toLowerCase() !== searchValue.toLowerCase().trim()) // search term will be displayed as a suggestion
    .map(({
      term,
      language
    }, index) => ({
      term: term.toLowerCase(),
      language,
      value: term,
      index: index + 1 // need to add 1 to the index to account for the search term
    }))]
  } : Object.assign({}, searchQueryTerm, {
    text: `- ${I18n.text('learningCenter.autocomplete.noSuggestions')}`
  }),
  // if there are no popular terms, search term is the only option
  {
    text: I18n.text('learningCenter.autocomplete.popularContent').toUpperCase(),
    value: 'popularContent',
    options: quickLinks.map((pt, index) => Object.assign({}, pt, {
      value: pt.resourceName,
      index
    }))
  }];
};
const useMainSearchInputWithAutocomplete = searchValue => {
  const debouncedSearchValue = useDebouncedValue(searchValue === null || searchValue === void 0 ? void 0 : searchValue.trim(), SEARCH_DEBOUNCE_TIMEOUT);
  const {
    suggestions,
    suggestionsLoading
  } = useSearchAutocomplete({
    queryTerm: debouncedSearchValue,
    language: getLang(),
    skip: debouncedSearchValue.length < SEARCH_MIN_QUERY_SIZE
  });
  const displayedSuggestions = useDisplayedData(suggestions, {
    loading: suggestionsLoading
  });

  // required mapping for the SearchWithAutocomplete component
  const options = useMemo(() => displayedSuggestions ? mapSuggestionsToAutocomplete(Object.assign({
    searchValue
  }, displayedSuggestions)) : [], [displayedSuggestions, searchValue]);
  return {
    options,
    debouncedSearchValue
  };
};
export const MainSearchInputWithAutocompleteProvider = ({
  children,
  appearsIn = 'heading-nav'
}) => {
  const [{
    search: searchParam = ''
  }] = useSearchParams();
  const [searchValue, setSearchValue] = useState(searchParam);
  const {
    options,
    debouncedSearchValue
  } = useMainSearchInputWithAutocomplete(searchValue);
  useEffect(() => {
    if (debouncedSearchValue.length >= SEARCH_MIN_QUERY_SIZE) {
      UsageTracker.track('interaction', {
        action: 'main-search-input',
        search: searchValue,
        appearsIn
      });
    }
  }, [debouncedSearchValue, appearsIn, searchValue]);
  return /*#__PURE__*/_jsx(SearchWithAutocompleteProvider, {
    onSearch: setSearchValue,
    options: options,
    searchValue: searchValue,
    children: children
  });
};